import { PlayerInfoFragment, PlayerProjectionFragment } from '@/api/events/query.generated';
import { EventInfo, EventInfoWithoutPlayers, isIndividualEvent, isTeamEvent } from '@/api/events/types/types';
import { ds } from '@/components/lib/design.system';
import { AppColors } from '@/components/lib/theme';
import { PlayerWithTeam } from '@/feature/betslip-pickem/types';
import { createPlayerEventKey } from '@/feature/betslip-pickem/utils/markets-cache-store-utils';
import { filterByPositionFunction, filterByProjectionFunction } from '@/feature/lobby/hooks/use-player-filters';
import { ALL_POSITIONS } from '@/feature/lobby/utils/filters';
import { checkIfPromoProjection } from '@/feature/lobby/utils/lineupsUtils';
import { EventAttributesType, EventStatus, PlayerAttributesType, PositionFilters, Result } from '@/types/api.generated';
import { filterPlayers, mapPlayers, sortPlayers, sortPlayersInProjectionView } from '@/utils/sortPlayers';
import { getTennisMatchup } from '@/utils/tennis-matchup';
import { ResponsiveValue } from '@shopify/restyle';
import { parseISO } from 'date-fns';

import { NFL_FULL_SEASON } from '../constants';
import { findEventAttribute, findPlayerAttribute } from '../fantasy-attribute-utils';
import { isESport, isPGA, isTennis, isUFC, nflFullSeason } from '../league';
import { renderDate } from '../renderDate';

export const gameUtils = {
    hasEnded: (status: EventStatus) => [EventStatus.Final, EventStatus.Overtime, EventStatus.Shootout].includes(status),

    isDelayed: (game: EventInfoWithoutPlayers) => {
        const parsedDate = parseISO(game.date);
        const shouldBeStarted = parsedDate < new Date();
        return game.status === EventStatus.Delayed || (shouldBeStarted && game.status === EventStatus.Scheduled);
    },

    isLive: (game: EventInfoWithoutPlayers) => {
        return isPGA(game.league) || nflFullSeason(game.league)
            ? false
            : [EventStatus.InProgress].includes(game.status);
    },

    createTitleLabel: (game: EventInfoWithoutPlayers): string => {
        if (isTeamEvent(game)) {
            const [away, home] = game.teams;
            let mark = '@';
            if (nflFullSeason(away.league)) {
                mark = '&';
            } else if (isUFC(away.league) || isESport(away.league)) {
                mark = 'vs';
            }
            return `${away.name} ${mark} ${home.name}`;
        } else if (isIndividualEvent(game)) {
            if (isTennis(game.league)) {
                const [player1, player2] = getTennisMatchup(game.players, game.name);
                return `${player1.lastName ?? ''} vs ${player2.lastName ?? ''}`;
            }
            return game.name ?? '';
        }
        return '';
    },

    createHeaderTitleLabel: (game: EventInfoWithoutPlayers): string => {
        if (isUFC(game.league)) {
            const tournamentName = findEventAttribute(game, EventAttributesType.Name);
            return tournamentName ?? '';
        }
        return game.name ?? '';
    },

    createTimeLabel: (game: EventInfoWithoutPlayers, player?: PlayerInfoFragment, useTeeTime = true): string => {
        const parsedDate = parseISO(game.date);
        const dayName = parsedDate.toLocaleDateString('default', { weekday: 'short' });
        if (gameUtils.isLive(game)) {
            return 'LIVE';
        }
        if (nflFullSeason(game.league)) {
            return '';
        }
        if (isPGA(game.league) && useTeeTime) {
            const teeTime = findPlayerAttribute(player, PlayerAttributesType.TeeTime);
            if (!player) {
                return '';
            }
            //Show tee time for PGA players, if available
            if (teeTime) {
                return renderDate(parseISO(teeTime));
            } else {
                return `${dayName}, No Tee Time`;
            }
        }

        if (gameUtils.isDelayed(game)) {
            return 'Starting soon';
        }
        return renderDate(parsedDate);
    },

    createTournamentTimeLabel: (game: EventInfoWithoutPlayers): string => {
        const tournamentStartDate = findEventAttribute(game, EventAttributesType.TournamentStartDateTime);
        return tournamentStartDate ? renderDate(parseISO(tournamentStartDate)) : '';
    },

    formatTournamentDates: (start: string, end: string): string => {
        const startDate = new Date(start);
        const endDate = new Date(end);

        const startMonth = startDate.toLocaleString('default', { month: 'short' });
        const endMonth = endDate.toLocaleString('default', { month: 'short' });

        const startDay = startDate.getDate();
        const endDay = endDate.getDate();

        if (start === end) {
            return `${startDay} ${startMonth}`;
        }
        if (startMonth === endMonth) {
            return `${startMonth} ${startDay} - ${endDay}`;
        }
        return `${startDay} ${startMonth} - ${endDay} ${endMonth}`;
    },

    createInfoLabel: (game: EventInfoWithoutPlayers): string => {
        const { venueDetails } = game || {};
        const { city = '', country = '', name = '' } = venueDetails || {};

        const location = [city, country].filter(Boolean).join(', ');
        return [name, location].filter(Boolean).join(' • ');
    },

    createStatusLabel: (eventStatus: EventStatus, result?: Result) => {
        if (result === Result.Void) {
            return '';
        }
        if (gameUtils.hasEnded(eventStatus)) {
            return 'Final';
        }
        if (eventStatus === EventStatus.InProgress) {
            return 'Current';
        }
        return 'Not Started';
    },

    createEventSubtitle: (game: EventInfoWithoutPlayers): string => {
        if (isTennis(game.league)) {
            return findEventAttribute(game, EventAttributesType.Name) ?? '';
        }
        return '';
    },

    getTeamInfoText: (event: EventInfo, player?: PlayerInfoFragment): string => {
        // if it's a UFC or Tennis event, show the opponent if there are 2 players
        if (isIndividualEvent(event) && (isUFC(event.league) || isTennis(event.league))) {
            // for tennis, the BE may not return both players, so derive the matchup from the event name
            if (isTennis(event.league)) {
                const [p1, p2] = getTennisMatchup(event.players ?? [], event.name);
                const isPlayer1 =
                    (!!p1.player?.id && p1.player.id === player?.id) ||
                    (!!p1.lastName && p1.lastName.toLowerCase() === player?.lastName?.toLowerCase());
                const opponent = isPlayer1 ? p2.lastName : p1.lastName;
                return opponent ? `vs ${opponent}` : '';
            }
            if (event?.players?.length !== 2) {
                return '';
            } else {
                const opponent = event.players.find(individualPlayer => individualPlayer.id !== player?.id)?.lastName;
                return opponent ? `vs ${opponent}` : '';
            }
        }

        // if player is from away team - show "@ <home team abbrev>"
        // if player is from home team - show "vs <away team abbrev>”
        const [away, home] = (isTeamEvent(event) && event.teams) || [];

        if (!away || !player) {
            return '';
        }
        const playsAway = !!away?.players?.find(awayPlayer => awayPlayer.id === player.id);

        // for esports the prefix is always 'vs {opponent team}'
        const playAwayPrefix = isESport(event.league) ? 'vs' : '@';
        return playsAway ? `${playAwayPrefix} ${home?.name}` : `vs ${away?.name}`;
    },

    extractAllPlayersFromEvents: (events: EventInfo[]) => {
        return events.reduce(
            (acc: (PlayerWithTeam & { isAway: boolean; eventStartTime: string; eventId: string })[], event) => {
                const [away, home] = isTeamEvent(event) ? event.teams : [];
                const individual = (isIndividualEvent(event) ? event.players : []) ?? [];

                const mappedIndividualPlayers = individual.map(player => ({
                    ...player,
                    league: event.league,
                    sport: event.sport,
                    isAway: false,
                    eventId: event.id,
                    eventStartTime: event.date,
                }));
                const mappedAwayPlayers = mapPlayers(away, event.league).map(player => ({
                    ...player,
                    isAway: true,
                    eventId: event.id,
                    eventStartTime: event.date,
                }));
                const mappedHomePlayers = mapPlayers(home, event.league).map(player => ({
                    ...player,
                    isAway: false,
                    eventId: event.id,
                    eventStartTime: event.date,
                }));
                return [...acc, ...mappedIndividualPlayers, ...mappedAwayPlayers, ...mappedHomePlayers];
            },
            []
        );
    },

    filterAndSortPlayersOnProjectionView: (
        events: EventInfo[],
        markets: Record<string, PlayerProjectionFragment[]>,
        playerProjectionFilter: string
    ) => {
        const playersFromEvents = gameUtils.extractAllPlayersFromEvents(events);
        const filterFunction = filterByProjectionFunction(playerProjectionFilter);
        const allPlayers = playersFromEvents
            //Map store projections to each player
            //This ensures that projectionss received via WS updates are also taken into
            //account when filter & grouping players by projections
            .map(it => {
                const key = createPlayerEventKey(it.id, it.eventId);
                const playerProjections = markets[key];
                return {
                    ...it,
                    projections: playerProjections,
                };
            })
            .filter(filterFunction);
        const playersSplitByProjection = allPlayers.flatMap(p => [
            ...gameUtils.splitPlayerByProjections(p, { playerProjectionFilter, mode: 'projections-tab' }),
        ]);

        return playersSplitByProjection.sort((firstPlayer, secondPlayer) => {
            return sortPlayersInProjectionView(firstPlayer, secondPlayer, playerProjectionFilter);
        });
    },

    /**
     * Splits a player into multiple PlayerWithTeam instances based on their projections and display mode.
     *
     * Promo projections (ANCHOR, FREE_PICK, NUKE, SPECIAL) are always split into individual player instances.
     * Alternative projections (BOOSTED, MINI_BOOSTED, SUPER_BOOSTED, REGULAR, EDGE_1, EDGE_2, EDGE_3) are grouped together.
     *
     * Behavior varies by mode:
     * - 'projections-tab': Filters projections by the provided playerProjectionFilter and always includes alternative projections
     * - 'popular-tab': No filtering applied, but only shows alternative projections if no promo projections exist for the player
     *
     * @param player - The player to split into multiple instances
     * @param opts - Configuration object containing:
     *   - mode: 'projections-tab' | 'popular-tab' - determines filtering and grouping behavior
     *   - playerProjectionFilter: string (required for projections-tab) - filter key to match against projection.key
     *
     * @returns Array of player instances, each containing a subset of the original player's projections
     */
    splitPlayerByProjections: <T extends PlayerWithTeam>(
        player: T,
        opts: { playerProjectionFilter: string; mode: 'projections-tab' } | { mode: 'popular-tab' }
    ): T[] => {
        const promoProjections: PlayerProjectionFragment[] = [];
        const alternativeProjections: PlayerProjectionFragment[] = [];
        const mode = opts.mode;
        const playerProjectionFilter = mode === 'projections-tab' ? opts.playerProjectionFilter : undefined;
        const projectionFilteringDisabled = playerProjectionFilter === undefined;

        // Categorize & filter projections
        player.projections.forEach(projection => {
            if (checkIfPromoProjection(projection.type)) {
                // Only include promo projections that match the filter
                if (projectionFilteringDisabled || projection.key === playerProjectionFilter) {
                    promoProjections.push(projection);
                }
            } else {
                // Only include alternative projections that match the filter
                if (projectionFilteringDisabled || projection.key === playerProjectionFilter) {
                    alternativeProjections.push(projection);
                }
            }
        });

        const splitPlayers: T[] = [];

        // Create individual player instances for each promo projection
        promoProjections.forEach(projection => {
            splitPlayers.push({
                ...player,
                projections: [projection],
            });
        });

        // On projections tab, we always want to show the alternative projections as well
        // On popular tab, only show alternatives if there are no promo projections for this player
        if (mode === 'projections-tab' || promoProjections.length === 0) {
            if (alternativeProjections.length > 0) {
                splitPlayers.push({
                    ...player,
                    projections: alternativeProjections,
                });
            }
        }

        return splitPlayers;
    },

    filterAndSortPlayers: (
        event: EventInfo,
        positionFilters: PositionFilters[] = [],
        positionFilter: string | undefined
    ): PlayerWithTeam[] => {
        const filterFunction = positionFilter !== ALL_POSITIONS ? filterByPositionFunction(positionFilter) : undefined;
        if (isTeamEvent(event)) {
            const [away, home] = event.teams || [];

            let awayPlayersList = mapPlayers(away, event.league);
            let homePlayersList = mapPlayers(home, event.league);

            if (filterFunction) {
                awayPlayersList = awayPlayersList.filter(filterFunction);
                homePlayersList = homePlayersList.filter(filterFunction);
            }

            awayPlayersList.sort((firstPlayer, secondPlayer) =>
                sortPlayers(firstPlayer, secondPlayer, positionFilters)
            );
            homePlayersList.sort((firstPlayer, secondPlayer) =>
                sortPlayers(firstPlayer, secondPlayer, positionFilters)
            );

            return [...filterPlayers(awayPlayersList ?? []), ...filterPlayers(homePlayersList ?? [])];
        } else if (isIndividualEvent(event)) {
            //individual event no sorting at the moment
            const players =
                event.players?.map(player => ({ ...player, league: event.league, sport: event.sport })) ?? [];
            return [...(filterPlayers(players) ?? [])];
        }
        return [];
    },

    createStatusWithValueLabel: (status: '' | 'Final' | 'Current' | 'Not Started', value?: number) => {
        if (status === '' || status === 'Not Started') {
            return undefined;
        }

        return `${status}: ${value ?? '-'}`;
    },

    createCommonEventDescription: (event: EventInfo, player?: PlayerInfoFragment) => {
        const versusText = gameUtils.getTeamInfoText(event, player);
        const eventTime = gameUtils.createTimeLabel(event, player);
        return { versusText, eventTime };
    },

    /* returns properties for event description with status */
    createEventDescriptionWithStatus: (
        event: EventInfo,
        player: PlayerInfoFragment | undefined,
        projectionStatus: {
            currentValue?: number;
            result?: Result;
        }
    ) => {
        const { currentValue, result } = projectionStatus;
        const { versusText, eventTime } = gameUtils.createCommonEventDescription(event, player);
        const status = gameUtils.createStatusLabel(event.status, result);
        const statusWithValueLabel = gameUtils.createStatusWithValueLabel(status, currentValue);
        const eventDetails = statusWithValueLabel || eventTime;

        return {
            versusText: nflFullSeason(event.league) ? NFL_FULL_SEASON : versusText,
            eventTime: eventDetails,
            tournamentName: '',
        };
    },

    getTournamentName: (event: EventInfo) => {
        if (isPGA(event.league)) {
            return event.name ?? '';
        }
        if (isTennis(event.league)) {
            return findEventAttribute(event, EventAttributesType.Name) ?? '';
        }
        return '';
    },

    /*creates and displays description with event time and tournament name if its present*/
    createEventDescriptionWithoutStatus: (event: EventInfo, player: PlayerInfoFragment | undefined) => {
        const { versusText, eventTime } = gameUtils.createCommonEventDescription(event, player);
        const tournamentName = gameUtils.getTournamentName(event);

        return {
            versusText: nflFullSeason(event.league) ? NFL_FULL_SEASON : versusText,
            eventTime,
            tournamentName,
        };
    },

    /* returns properties for event description with status or without status
        - versusText
        - eventTime
        - tournamentName
    */
    createEventDescriptionLabel: (options: {
        projectionStatus?: {
            currentValue?: number;
            result?: Result;
        };
        event?: EventInfo;
        player?: PlayerInfoFragment;
    }) => {
        const { projectionStatus, event, player } = options;
        if (!event) {
            return {
                versusText: '-',
                eventTime: '-',
                tournamentName: '-',
            };
        }

        return projectionStatus
            ? gameUtils.createEventDescriptionWithStatus(event, player, projectionStatus)
            : gameUtils.createEventDescriptionWithoutStatus(event, player);
    },

    getEventInfoTextColor: (
        isVoided?: boolean,
        shouldHighlightLiveEvent?: boolean,
        color?: ResponsiveValue<AppColors, {}>,
        isSuspended?: boolean,
        viewMode?: 'row' | 'tile',
        isStartingSoon?: boolean
    ) => {
        if (viewMode === 'tile') {
            if (shouldHighlightLiveEvent || isStartingSoon) {
                return ds.colorToken.text.danger;
            }
            return 'neutral300';
        }

        if (isSuspended || isVoided) {
            return 'neutral400';
        }
        if (shouldHighlightLiveEvent || isStartingSoon) {
            return ds.colorToken.text.danger;
        }
        return color || 'neutral300';
    },
};
